import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", address: "", driver_license: "", source: "", notes: "" };

export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");

  const load = async () => { const r = await api.get("/customers"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    if (editing) await api.put(`/customers/${editing.id}`, form);
    else await api.post("/customers", form);
    toast.success("Saved"); setOpen(false); load();
  };

  const filtered = items.filter((c) => !q || `${c.name} ${c.phone} ${c.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="customers-page">
      <PageHeader
        overline="CRM"
        title="Customers"
        description="Buyer database with purchase history and repeat-buyer tracking."
        action={<Button data-testid="add-customer" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Plus size={16} className="mr-2" />Add Customer</Button>}
      />

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…" className="pl-9 rounded-lg" />
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Name", "Phone", "Email", "Source", "Purchases", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No customers yet.</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-3"><Link to={`/customers/${c.id}`} className="font-semibold hover:text-blue-600">{c.name}</Link><div className="text-xs text-muted-foreground">{c.driver_license ? `DL ${c.driver_license}` : ""}</div></td>
                <td className="px-4 py-3">{c.phone || "—"}</td>
                <td className="px-4 py-3">{c.email || "—"}</td>
                <td className="px-4 py-3">{c.source || "—"}</td>
                <td className="px-4 py-3">{c.purchases > 1 ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs font-semibold">{c.purchases} (repeat)</span> : c.purchases}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`edit-customer-${c.id}`} onClick={() => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); }} className="p-1.5 rounded-md hover:bg-secondary"><Pencil size={14} /></button>
                    {user?.role === "admin" && <button onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/customers/${c.id}`); load(); } }} className="p-1.5 rounded-md hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit" : "Add"} Customer</DialogTitle>
            <DialogDescription>Store buyer details for invoicing and repeat-business tracking.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Phone</Label><Input data-testid="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Email</Label><Input data-testid="c-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Driver License</Label><Input data-testid="c-dl" value={form.driver_license} onChange={(e) => setForm({ ...form, driver_license: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Source</Label><Input data-testid="c-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Walk-in, Referral, Online…" className="rounded-lg" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Textarea data-testid="c-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-lg" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea data-testid="c-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-lg" /></div>
          </div>
          <DialogFooter><Button data-testid="c-save" onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
